import { z } from 'zod'

import { presentationFor } from '@/services/api/presentationFor'
import { zodIssueToFieldIssues } from '@/services/api/contracts/zodIssueToFieldIssues'
import { ApiErrorCodeSchema, ApiProblemSchema } from '@/services/api/generated'
import type { ApiErrorCode } from '@/services/api/generated'
import type { ApiFieldIssue } from './ApiFieldIssue'

const internalErrorCode: ApiErrorCode = ApiErrorCodeSchema.parse('INTERNAL_ERROR')
const validationErrorCode: ApiErrorCode = ApiErrorCodeSchema.parse('VALIDATION_ERROR')
const internalErrorPresentation = presentationFor(internalErrorCode)
const INTERNAL_ERROR_DETAIL = internalErrorPresentation.safeDetail
const INTERNAL_ERROR_TITLE = internalErrorPresentation.title
const INTERNAL_ERROR_TYPE = 'https://api.mealzusa.com/problems/internal-error'
const INTERNAL_ERROR_STATUS = 500

type ApiProblem = z.infer<typeof ApiProblemSchema>

const transportProblemSchema = z.union([
  z
    .looseObject({
      data: z.unknown()
    })
    .transform(({ data }) => data),
  z
    .looseObject({
      response: z.looseObject({
        _data: z.unknown()
      })
    })
    .transform(({ response }) => response._data),
  z
    .looseObject({
      response: z.looseObject({
        data: z.unknown()
      })
    })
    .transform(({ response }) => response.data)
])

/**
 * An ApiProblem as this model holds it, with request_id optional so a failure
 * the client detects locally can be represented without inventing one.
 */
type LocalProblem = Omit<ApiProblem, 'request_id'> & { request_id: string | null }

/**
 * Converts an RFC 6901 JSON Pointer ("/items/0/name") back to the Laravel
 * dot-notation field path ("items.0.name") that form `fieldMap` entries use.
 */
const pointerToFieldPath = (pointer: string): string =>
  pointer
    .replace(/^\//, '')
    .split('/')
    .map((segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~'))
    .join('.')

const groupFieldIssuesByPath = (
  fieldIssues: ApiProblem['field_issues']
): Readonly<Record<string, readonly ApiFieldIssue[]>> => {
  const byPath: Record<string, ApiFieldIssue[]> = {}

  for (const issue of fieldIssues ?? []) {
    const path = pointerToFieldPath(issue.source.pointer)
    ;(byPath[path] ??= []).push(issue)
  }

  return Object.freeze(
    Object.fromEntries(
      Object.entries(byPath).map(([path, issues]) => [path, Object.freeze(issues)])
    )
  )
}

export class AppProblemModel extends Error {
  public readonly code: ApiErrorCode
  declare public readonly context: string | null
  public readonly detail: string
  public readonly fieldIssues: Readonly<Record<string, readonly ApiFieldIssue[]>>
  public readonly retryable: boolean
  public readonly retryDelaySeconds: number | null
  public readonly requestId: string | null
  public readonly status: number
  public readonly instance: string | null
  public readonly type: string
  public readonly title: string
  public readonly reason: string | null
  public readonly params: Readonly<Record<string, string | number | boolean>> | null

  private constructor(problem: LocalProblem | null, context: string | null) {
    super('API request failed')

    this.code = problem?.code ?? internalErrorCode
    Object.defineProperty(this, 'context', {
      configurable: false,
      enumerable: false,
      value: context,
      writable: false
    })
    this.detail = problem?.detail ?? INTERNAL_ERROR_DETAIL
    this.fieldIssues = groupFieldIssuesByPath(problem?.field_issues)
    this.retryable = problem?.retryable ?? false
    this.retryDelaySeconds = problem?.retry_after_seconds ?? null
    this.requestId = problem?.request_id ?? null
    this.status = problem?.status ?? INTERNAL_ERROR_STATUS
    this.instance = problem?.instance ?? null
    this.type = problem?.type ?? INTERNAL_ERROR_TYPE
    this.title = problem?.title ?? INTERNAL_ERROR_TITLE
    this.reason = problem?.reason ?? null
    this.params = problem?.params !== undefined ? Object.freeze({ ...problem.params }) : null

    Object.freeze(this)
  }

  /** The 409 a partner-scoped route returns while the account owner has not created a brand. */
  public get isBrandSetupRequired(): boolean {
    return this.code === 'CONFLICT' && this.reason === 'BRAND_SETUP_REQUIRED'
  }

  /**
   * A failure the client itself detects, carrying a message the API authored.
   *
   * Some endpoints report a domain refusal in a 200 body rather than as a
   * Problem Details response. That message is still the API's, so it becomes a
   * problem here instead of a bare Error, which normalize() could only render
   * as a generic INTERNAL_ERROR. requestId stays null because no correlation
   * id was issued for a failure the server reported as success.
   */
  public static fromDomainFailure(
    detail: string,
    diagnostic: Readonly<{ context: string }> | null = null
  ): AppProblemModel {
    return new AppProblemModel(
      {
        code: 'BUSINESS_RULE_VIOLATION',
        detail,
        instance: '',
        request_id: null,
        status: 422,
        title: 'Business rule violation',
        type: 'https://api.mealzusa.com/problems/business-rule-violation'
      },
      diagnostic?.context ?? null
    )
  }

  public static fromZodError(
    error: z.ZodError,
    diagnostic: Readonly<{ context: string }> | null = null
  ): AppProblemModel {
    const presentation = presentationFor(validationErrorCode)

    return new AppProblemModel(
      {
        code: validationErrorCode,
        detail: presentation.safeDetail,
        field_issues: error.issues.flatMap(zodIssueToFieldIssues),
        instance: '',
        request_id: null,
        status: 422,
        title: presentation.title,
        type: 'https://api.mealzusa.com/problems/validation-error'
      },
      diagnostic?.context ?? null
    )
  }

  public static normalize(
    error: unknown,
    diagnostic: Readonly<{ context: string }> | null = null
  ): AppProblemModel {
    if (error instanceof AppProblemModel) {
      return error
    }

    try {
      const directProblem = ApiProblemSchema.safeParse(error)
      if (directProblem.success) {
        return new AppProblemModel(directProblem.data, diagnostic?.context ?? null)
      }

      const transportProblem = transportProblemSchema.safeParse(error)
      if (transportProblem.success) {
        const containedProblem = ApiProblemSchema.safeParse(transportProblem.data)
        if (containedProblem.success) {
          return new AppProblemModel(containedProblem.data, diagnostic?.context ?? null)
        }
      }

      // Nuxt's useAsyncData/useFetch routes every
      // caught error through h3's createError(), which only passes an error
      // through unchanged when it is already an H3Error. An AppProblemModel is
      // a plain Error subclass, so it fails that check and gets rewrapped into
      // a new H3Error -- the original survives only as `.cause`. Unwrapping it
      // here means every useAsyncData-based caller still recovers the real
      // problem instead of a synthesized INTERNAL_ERROR with no field_issues.
      const cause = error instanceof Error ? error.cause : undefined
      if (cause !== undefined && cause !== error) {
        return AppProblemModel.normalize(cause, diagnostic)
      }

      return new AppProblemModel(null, diagnostic?.context ?? null)
    } catch {
      return new AppProblemModel(null, diagnostic?.context ?? null)
    }
  }
}
